/**
 * canvasTicket.ts — Renderiza el ticket de pedido BLE a un <canvas> 2D (imagen)
 * en lugar de texto ESC/POS plano.
 *
 * Motivación: el encoder de texto (`buildWebBleTicket.ts`) manda el ticket usando
 * la fuente interna de la térmica → se ve tosco y sólo el título cambia de tamaño.
 * pdfMake (web) y Electron rasterizan HTML a imagen y la mandan como raster
 * (GS v 0). Aquí dibujamos el MISMO contenido/orden que el ticket Electron en un
 * canvas y devolvemos el `HTMLCanvasElement`; el caller lo empaqueta a GS v 0 y lo
 * imprime. Así el ticket BLE se ve IGUAL, soporta los 5 niveles de tamaño por
 * elemento (título/subtítulo/cuerpo), logo y ñ/acentos perfectos (los dibuja el
 * navegador, sin code-pages).
 *
 * BEST-EFFORT: si no hay `document`/`Image` (jsdom) o algo falla (logo tainted por
 * CORS, etc.) devuelve `null` y el caller usa el fallback de texto. NUNCA se accede
 * a `document`/`Image`/`canvas` en el top-level.
 *
 * El CONTENIDO (condiciones, etiquetas, totales) replica exactamente la lógica del
 * encoder de texto (`buildWebBleTicket.ts`) y del ticket Electron
 * (`pages/Orders/utils/electronTicket/buildTicket.ts`): mismos helpers de formato,
 * mismos mapas, `computeOrderTotal` para "Total a pagar" y "Cambio".
 */

import { OrderDetail, ProductOrder } from '@interfaces/OrderTypes';
import { DELIVERY_MAP, ORDER_PAYMENT_MAP, PRINT_TEXT_SIZES_VALUES_MAP, TICKET_TYPE_MAP } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import { normalizePaymentMethod } from '@pages/Orders/utils/paymentUtils';
import { dotsForWidth, loadDrawableImage } from './raster';
import { separatorCols, WebBleTicketType } from './buildWebBleTicket';

/** Roles de tamaño disponibles por nivel (mismas keys que el ticket Electron). */
type TextRole = keyof (typeof PRINT_TEXT_SIZES_VALUES_MAP)['medium'];

export type CanvasTicketOptions = {
  /** Tipo de ticket: completo con precios o comanda de cocina simplificada. */
  ticketType: WebBleTicketType;
  /** Tamaño de texto (5 niveles): 'xxs' | 'small' | 'medium' | 'large' | 'xxl'. */
  textSize: keyof typeof PRINT_TEXT_SIZES_VALUES_MAP;
  /** Ancho del rollo en mm — define el ancho del canvas en puntos (384 / 576). */
  pageWidth?: 58 | 80;
  /** Si es false, omite las líneas separadoras decorativas. */
  decoration?: boolean;
  /** Si es false, no se intenta dibujar el logo. */
  showLogo?: boolean;
  /** Datos del comercio para el footer (sólo ticket normal). */
  commerceName?: string;
  commerceAddress?: string;
  /** URL del logo del comercio (CDN). Se carga vía fetch+createImageBitmap (sin depender del CORS del canvas). */
  logoUrl?: string | null;
  /** Etiqueta personalizada para el campo de referencias de dirección. */
  referencesLabel?: string;
  /** Flags de visibilidad — paridad con el ticket Electron. */
  showClientPhone?: boolean;
  /** Imprimir el NOMBRE del comercio. Aplica a caja Y cocina. Default true. */
  showCommerceName?: boolean;
  /** Imprimir la DIRECCIÓN del comercio (solo caja). Default true. */
  showCommerceAddress?: boolean;
};

const LEGAL_DISCLAIMER =
  'Este formato no es un comprobante de pago, ni contrato de venta, es para uso exclusivo del vendedor. Los precios que en el consignan son validos hasta la fecha indicada.';

// Factor de escala de los px HTML al lienzo en puntos. Los px de
// PRINT_TEXT_SIZES_VALUES_MAP están pensados para render HTML (~302pt de ancho útil
// @80mm en pdfMake). Para llenar el rollo de 576/384 dots escalamos proporcional al
// ancho: cada nivel se ve igual de "lleno" en ambos rollos.
const HTML_REFERENCE_WIDTH_PT = 200;

// Margen lateral del lienzo (en puntos).
const SIDE_PADDING = 8;
// Separación vertical extra entre líneas (en puntos, antes de escalar la fuente).
const LINE_GAP = 4;
// Logo: lado máximo en puntos (el Electron usa 110px; lo dibujamos centrado).
const LOGO_MAX_SIDE = 120;

// Alineación de una línea de texto.
type Align = 'left' | 'center' | 'right';

// Una línea simple de texto a dibujar.
type TextLine = {
  kind: 'text';
  text: string;
  role: TextRole;
  align: Align;
  bold: boolean;
};

// Una fila "etiqueta ......... valor" (nombre a la izq, precio a la derecha).
type RowLine = {
  kind: 'row';
  left: string;
  right: string;
  role: TextRole;
  bold: boolean;
  /** Sangría del texto izquierdo en puntos (extras indentados). */
  indent?: number;
};

// Una línea separadora horizontal (decoración).
type SepLine = { kind: 'sep' };

// Espacio vertical en blanco (en puntos, sin escalar).
type FeedLine = { kind: 'feed'; height: number };

// Línea lógica del ticket (texto/fila/separador/feed). Exportada para que el preview de
// MODO TEXTO la renderice en monoespaciado plano usando la MISMA fuente de contenido que
// el canvas/impresión (sin re-implementar el contenido → no diverge).
export type TicketLine = TextLine | RowLine | SepLine | FeedLine;

// ---- Lógica de contenido (replica buildWebBleTicket.ts) ----------------------

// Total que se IMPRIME ("Total a pagar"): subtotal - descuento + propina + envío.
// El "Cambio" se calcula contra ESTE total (no `pedido.total` del servidor) para
// ser coherente con lo impreso — misma decisión que el encoder de texto.
function computeOrderTotal(pedido: OrderDetail['pedido']): number {
  return (pedido.subtotal || 0) - (pedido.coupon_amount || 0) + (pedido.propina || 0) + (pedido.costo_envio || 0);
}

// Etiqueta del método de pago, replicando la lógica del ticket Electron.
function paymentLabel(order: OrderDetail): string {
  let label: string = ORDER_PAYMENT_MAP[order.pedido.metodo_pago]?.label || order.pedido.metodo_pago;
  if (normalizePaymentMethod(order.pedido.metodo_pago) === ORDER_PAYMENT_MAP.toConfirm.key) {
    label = order.pedido.metodo_entrega === 'inRestaurant' ? 'Por confirmar' : 'Pago en linea';
  }
  return label;
}

// ---- Construcción de la lista de líneas (sin tocar el DOM) --------------------

function pushProducts(lines: TicketLine[], products: ProductOrder[], decoration: boolean): void {
  for (const product of products) {
    const name = `${product.cantidad} x ${product.nombre} - ${product.presentacion}`;
    lines.push({ kind: 'row', left: name, right: formatToCurrency(product.subtotal), role: 'content', bold: true });
    if (product.instrucciones) {
      lines.push({ kind: 'text', text: product.instrucciones, role: 'content', align: 'left', bold: true });
    }
    for (const extra of product.extras) {
      lines.push({
        kind: 'row',
        left: `${extra.cantidad} x ${extra.nombre}`,
        right: formatToCurrency(extra.precio),
        role: 'content',
        bold: false,
        indent: 16,
      });
    }
    if (decoration) lines.push({ kind: 'sep' });
  }
}

function pushProductsSimplified(lines: TicketLine[], products: ProductOrder[], decoration: boolean): void {
  for (const product of products) {
    lines.push({
      kind: 'text',
      text: `${product.cantidad} x ${product.nombre} - ${product.presentacion}`,
      role: 'content',
      align: 'left',
      bold: true,
    });
    if (product.instrucciones) {
      lines.push({
        kind: 'text',
        text: `  Instrucciones: ${product.instrucciones}`,
        role: 'content',
        align: 'left',
        bold: true,
      });
    }
    for (const extra of product.extras) {
      lines.push({
        kind: 'text',
        text: `  + ${extra.cantidad} x ${extra.nombre}`,
        role: 'content',
        align: 'left',
        bold: false,
      });
    }
    if (decoration) lines.push({ kind: 'sep' });
  }
}

function pushFooterTotals(lines: TicketLine[], order: OrderDetail): void {
  const { pedido } = order;
  lines.push({
    kind: 'row',
    left: 'Subtotal',
    right: formatToCurrency(pedido.subtotal || 0),
    role: 'content',
    bold: false,
  });
  if (pedido.coupon_amount) {
    lines.push({
      kind: 'row',
      left: 'Descuento',
      right: `-${formatToCurrency(pedido.coupon_amount)}`,
      role: 'content',
      bold: false,
    });
  }
  if (pedido.propina) {
    lines.push({ kind: 'row', left: 'Propina', right: formatToCurrency(pedido.propina), role: 'content', bold: false });
  }
  // Solo en delivery (igual que pdfMake createOrderTotalsSection y el modo texto).
  if (pedido.metodo_entrega === 'delivery') {
    lines.push({
      kind: 'row',
      left: 'Costo de envio',
      right: formatToCurrency(pedido.costo_envio || 0),
      role: 'content',
      bold: false,
    });
  }
  lines.push({
    kind: 'row',
    left: 'Total a pagar',
    right: formatToCurrency(computeOrderTotal(pedido)),
    role: 'content',
    bold: true,
  });
}

function pushDeliveryAddress(lines: TicketLine[], order: OrderDetail, referencesLabel: string): void {
  const { pedido } = order;
  if (pedido.metodo_entrega !== 'delivery') return;
  lines.push({ kind: 'text', text: 'Domicilio de entrega:', role: 'content', align: 'left', bold: true });
  const push = (text: string): number =>
    lines.push({ kind: 'text', text, role: 'content', align: 'left', bold: false });
  if (pedido.colonia) push(`Zona o Colonia: ${pedido.colonia}`);
  if (pedido.calle) push(`Calle o Mza: ${pedido.calle}`);
  if (pedido.numero_exterior) push(`Numero Exterior: ${pedido.numero_exterior}`);
  if (pedido.numero_interior) push(`Numero interior: ${pedido.numero_interior}`);
  if (pedido.direccion_completa) push(`Direccion: ${pedido.direccion_completa}`);
  if (pedido.referencias) push(`${referencesLabel}: ${pedido.referencias}`);
}

// Lista ORDENADA de líneas del ticket NORMAL (ver CONTRATO §1).
export function buildNormalLines(order: OrderDetail, options: CanvasTicketOptions): TicketLine[] {
  const { pedido } = order;
  const lines: TicketLine[] = [];
  const decoration = options.decoration !== false;
  const referencesLabel = options.referencesLabel || 'Referencias';
  const showClientPhone = options.showClientPhone !== false;
  const showCommerceName = options.showCommerceName !== false;
  const showCommerceAddress = options.showCommerceAddress !== false;

  lines.push({ kind: 'text', text: 'Ticket pedido', role: 'title', align: 'center', bold: true });
  lines.push({ kind: 'text', text: `No. ${pedido.folio}`, role: 'title', align: 'center', bold: true });
  lines.push({ kind: 'feed', height: 8 });

  if (pedido.nombre) {
    lines.push({ kind: 'text', text: 'Cliente:', role: 'content', align: 'center', bold: true });
    lines.push({ kind: 'text', text: pedido.nombre, role: 'subTitle', align: 'center', bold: true });
  }
  if (showClientPhone && pedido.numero_celular) {
    const code = pedido.country_code ? `+${pedido.country_code} ` : '';
    lines.push({
      kind: 'text',
      text: `Celular: ${code}${pedido.numero_celular}`,
      role: 'medium',
      align: 'center',
      bold: true,
    });
  }
  const tableNumber = pedido.numero_mesa || pedido.self_service?.table_number;
  if (tableNumber) {
    lines.push({ kind: 'text', text: `Mesa: ${tableNumber}`, role: 'content', align: 'center', bold: true });
  }
  lines.push({ kind: 'feed', height: 8 });

  lines.push({ kind: 'text', text: 'Detalle del pedido:', role: 'content', align: 'center', bold: true });
  if (decoration) lines.push({ kind: 'sep' });
  pushProducts(lines, order.productos, decoration);
  pushFooterTotals(lines, order);
  lines.push({ kind: 'feed', height: 8 });

  if (pedido.comentario) {
    lines.push({
      kind: 'text',
      text: `Indicaciones: ${pedido.comentario}`,
      role: 'content',
      align: 'left',
      bold: true,
    });
  }
  lines.push({
    kind: 'text',
    text: `Metodo de entrega: ${DELIVERY_MAP[pedido.metodo_entrega]?.label || pedido.metodo_entrega}`,
    role: 'content',
    align: 'left',
    bold: true,
  });
  lines.push({
    kind: 'text',
    text: `Tipo de pago: ${paymentLabel(order)}`,
    role: 'content',
    align: 'left',
    bold: true,
  });

  const isCash = normalizePaymentMethod(pedido.metodo_pago) === ORDER_PAYMENT_MAP.efectivo.key;
  if (pedido.cliente_paga_con && isCash) {
    const paidWith = parseFloat(pedido.cliente_paga_con || '0');
    lines.push({
      kind: 'text',
      text: `Cliente paga con: ${formatToCurrency(paidWith)}`,
      role: 'content',
      align: 'left',
      bold: true,
    });
    lines.push({
      kind: 'text',
      text: `Cambio: ${formatToCurrency(paidWith - computeOrderTotal(pedido))}`,
      role: 'content',
      align: 'left',
      bold: true,
    });
  }

  pushDeliveryAddress(lines, order, referencesLabel);

  if (decoration) lines.push({ kind: 'sep' });
  // Nombre gateado por showCommerceName; dirección por showCommerceAddress (independientes).
  if (showCommerceName && options.commerceName) {
    lines.push({ kind: 'text', text: options.commerceName, role: 'content', align: 'center', bold: true });
  }
  if (showCommerceAddress && options.commerceAddress) {
    lines.push({ kind: 'text', text: options.commerceAddress, role: 'small', align: 'center', bold: false });
  }
  lines.push({
    kind: 'text',
    text: `Fecha y hora: ${new Date(pedido.created_at).toLocaleString()}`,
    role: 'small',
    align: 'center',
    bold: true,
  });
  lines.push({ kind: 'text', text: LEGAL_DISCLAIMER, role: 'small', align: 'center', bold: false });

  return lines;
}

// Lista ORDENADA de líneas de la COMANDA DE COCINA (ver CONTRATO §2).
export function buildSimplifiedLines(order: OrderDetail, options: CanvasTicketOptions): TicketLine[] {
  const { pedido } = order;
  const lines: TicketLine[] = [];
  const decoration = options.decoration !== false;

  lines.push({ kind: 'text', text: 'Ticket de Cocina', role: 'title', align: 'center', bold: true });
  lines.push({ kind: 'text', text: `No. ${pedido.folio}`, role: 'title', align: 'center', bold: true });
  lines.push({
    kind: 'text',
    text: `Metodo: ${DELIVERY_MAP[pedido.metodo_entrega]?.label || pedido.metodo_entrega}`,
    role: 'content',
    align: 'center',
    bold: true,
  });
  const tableNumber = pedido.numero_mesa || pedido.self_service?.table_number;
  if (tableNumber) {
    lines.push({ kind: 'text', text: `Mesa: ${tableNumber}`, role: 'content', align: 'center', bold: true });
  }
  lines.push({ kind: 'feed', height: 8 });

  lines.push({ kind: 'text', text: 'Productos:', role: 'content', align: 'center', bold: true });
  pushProductsSimplified(lines, order.productos, decoration);

  if (pedido.comentario) {
    lines.push({
      kind: 'text',
      text: `Indicaciones: ${pedido.comentario}`,
      role: 'content',
      align: 'left',
      bold: true,
    });
  }

  if (decoration) lines.push({ kind: 'sep' });
  // Nombre del comercio: gateado por showCommerceName (aplica también a la comanda).
  if (options.showCommerceName !== false && options.commerceName) {
    lines.push({ kind: 'text', text: options.commerceName, role: 'content', align: 'center', bold: true });
  }
  lines.push({
    kind: 'text',
    text: `Fecha y hora: ${new Date(pedido.created_at).toLocaleString()}`,
    role: 'small',
    align: 'center',
    bold: true,
  });

  return lines;
}

// ---- Render en canvas (toca el DOM; sólo dentro de funciones con guard) --------

// Tamaño de fuente en puntos para un rol, escalado al ancho del rollo.
function fontPx(role: TextRole, textSize: CanvasTicketOptions['textSize'], scale: number): number {
  const raw = PRINT_TEXT_SIZES_VALUES_MAP[textSize][role]; // p.ej. '17px'
  const px = parseInt(raw, 10) || 12;
  return Math.max(8, Math.round(px * scale));
}

function setFont(ctx: CanvasRenderingContext2D, sizePx: number, bold: boolean): void {
  ctx.font = `${bold ? 'bold ' : ''}${sizePx}px Arial, sans-serif`;
}

// Parte un texto en líneas que caben en `maxWidth` (wrap por palabras; si una
// palabra sola no cabe, se trocea por caracteres).
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  // Trocea por caracteres una palabra que no cabe en `maxWidth`: empuja a `out` los
  // segmentos completos y devuelve el último (parcial) para seguir acumulando.
  const charWrap = (word: string): string => {
    let chunk = '';
    for (const ch of word) {
      if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
        out.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    return chunk;
  };
  const paragraphs = text.split('\n');
  for (const paragraph of paragraphs) {
    const words = paragraph.split(' ');
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        line = candidate;
      } else if (!line) {
        // Palabra sola al inicio de línea que no cabe → trocear.
        line = charWrap(word);
      } else {
        // La línea actual ya tiene contenido: ciérrala y empieza con la palabra,
        // troceándola TAMBIÉN si por sí sola no cabe (antes se emitía entera y se recortaba).
        out.push(line);
        line = ctx.measureText(word).width <= maxWidth ? word : charWrap(word);
      }
    }
    out.push(line);
  }
  return out;
}

// Geometría derivada del ancho del rollo, reutilizada por medición y dibujo.
type Geometry = {
  dots: number;
  scale: number;
  contentWidth: number;
  sepWidth: number;
  lineGap: number;
  sepThickness: number;
  sepHeight: number;
};

function geometryFor(pageWidth: 58 | 80 | undefined): Geometry {
  const dots = dotsForWidth(pageWidth);
  const scale = dots / HTML_REFERENCE_WIDTH_PT;
  const lineGap = Math.round(LINE_GAP * scale);
  const sepThickness = Math.max(1, Math.round(scale));
  const contentWidth = dots - SIDE_PADDING * 2;
  // El separador usa el MISMO ancho conservador que el modo texto (columnas−1) para que
  // ambos modos se vean consistentes: ocupa separatorCols/(separatorCols+1) del ancho útil.
  const sepCols = separatorCols(pageWidth);
  const sepWidth = Math.round((contentWidth * sepCols) / (sepCols + 1));
  return {
    dots,
    scale,
    contentWidth,
    sepWidth,
    lineGap,
    sepThickness,
    sepHeight: sepThickness + lineGap,
  };
}

// Elemento ya medido y listo para pintar (filas envueltas + tamaños en puntos).
type Drawable =
  | { type: 'lines'; rows: string[]; sizePx: number; bold: boolean; align: Align; indent: number }
  | { type: 'row'; left: string[]; right: string; sizePx: number; bold: boolean; indent: number }
  | { type: 'sep' }
  | { type: 'feed'; height: number };

// Logo ya cargado y escalado (o null si no aplica/falla).
// El logo ya rasterizado a `ImageData` (píxeles leídos), NO la imagen cruda. Así se
// compone en el ticket con `putImageData` (copia píxeles ya leídos) y NUNCA propaga el
// taint al canvas del ticket.
type LoadedLogo = { data: ImageData; w: number; h: number };

// Carga el logo y lo rasteriza en un canvas AISLADO (offscreen). Prueba `getImageData`
// SOBRE ESE canvas: si el logo es cross-origin sin CORS, el probe lanza → devolvemos null
// y el ticket se imprime SIN logo, pero el canvas del ticket queda intacto (NUNCA colapsa
// a texto plano por culpa del logo). El loader robusto (fetch+createImageBitmap) recupera
// el logo en el caso común; el aislamiento es la red de seguridad.
async function loadLogo(options: CanvasTicketOptions, contentWidth: number): Promise<LoadedLogo | null> {
  if (options.showLogo === false || !options.logoUrl) return null;
  try {
    const img = await loadDrawableImage(options.logoUrl);
    const base = img.width || LOGO_MAX_SIDE;
    const ratio = Math.min(1, LOGO_MAX_SIDE / base, contentWidth / base);
    const w = Math.max(1, Math.round(base * ratio));
    const h = Math.max(1, Math.round((img.height || 1) * ratio));
    // Canvas OFFSCREEN aislado: el logo se dibuja AQUÍ, nunca en el canvas del ticket.
    const logoCanvas = document.createElement('canvas');
    logoCanvas.width = w;
    logoCanvas.height = h;
    const lctx = logoCanvas.getContext('2d');
    if (!lctx) return null;
    lctx.fillStyle = '#ffffff';
    lctx.fillRect(0, 0, w, h);
    lctx.drawImage(img, 0, 0, w, h);
    // PROBE: lanza si quedó tainted → catch → null (ticket sin logo, canvas del ticket sano).
    const data = lctx.getImageData(0, 0, w, h);
    return { data, w, h };
  } catch {
    return null;
  }
}

// Convierte una línea de texto en su `Drawable` envuelto.
function measureText(
  mctx: CanvasRenderingContext2D,
  line: TextLine,
  g: Geometry,
  textSize: CanvasTicketOptions['textSize'],
): Drawable {
  const sizePx = fontPx(line.role, textSize, g.scale);
  setFont(mctx, sizePx, line.bold);
  const rows = wrapText(mctx, line.text, g.contentWidth);
  return { type: 'lines', rows, sizePx, bold: line.bold, align: line.align, indent: 0 };
}

// Convierte una fila "etiqueta … valor" en su `Drawable` (izquierda envuelta).
function measureRow(
  mctx: CanvasRenderingContext2D,
  line: RowLine,
  g: Geometry,
  textSize: CanvasTicketOptions['textSize'],
): Drawable {
  const sizePx = fontPx(line.role, textSize, g.scale);
  setFont(mctx, sizePx, line.bold);
  const indent = Math.round((line.indent || 0) * g.scale);
  const rightWidth = mctx.measureText(line.right).width;
  const leftMaxWidth = Math.max(1, g.contentWidth - indent - rightWidth - Math.round(6 * g.scale));
  const left = wrapText(mctx, line.left, leftMaxWidth);
  return { type: 'row', left, right: line.right, sizePx, bold: line.bold, indent };
}

// Altura de un `Drawable` en puntos.
function drawableHeight(d: Drawable, g: Geometry): number {
  switch (d.type) {
    case 'lines':
      return d.rows.length * (d.sizePx + g.lineGap);
    case 'row':
      return d.left.length * (d.sizePx + g.lineGap);
    case 'sep':
      return g.sepHeight;
    case 'feed':
      return d.height;
  }
}

// Pasada de medición: convierte las líneas lógicas en `Drawable`s medidos.
function measureLines(
  mctx: CanvasRenderingContext2D,
  lines: TicketLine[],
  g: Geometry,
  textSize: CanvasTicketOptions['textSize'],
): Drawable[] {
  return lines.map((line) => {
    if (line.kind === 'sep') return { type: 'sep' };
    if (line.kind === 'feed') return { type: 'feed', height: Math.round(line.height * g.scale) };
    if (line.kind === 'text') return measureText(mctx, line, g, textSize);
    return measureRow(mctx, line, g, textSize);
  });
}

// Pinta una línea/párrafo de texto y devuelve la nueva coordenada `y`.
function paintLines(
  ctx: CanvasRenderingContext2D,
  d: Extract<Drawable, { type: 'lines' }>,
  g: Geometry,
  y: number,
): number {
  setFont(ctx, d.sizePx, d.bold);
  ctx.textAlign = d.align === 'center' ? 'center' : d.align === 'right' ? 'right' : 'left';
  const x = d.align === 'center' ? g.dots / 2 : d.align === 'right' ? g.dots - SIDE_PADDING : SIDE_PADDING;
  let ny = y;
  for (const row of d.rows) {
    ctx.fillText(row, x, ny);
    ny += d.sizePx + g.lineGap;
  }
  return ny;
}

// Pinta una fila "etiqueta … valor" y devuelve la nueva coordenada `y`.
function paintRow(
  ctx: CanvasRenderingContext2D,
  d: Extract<Drawable, { type: 'row' }>,
  g: Geometry,
  y: number,
): number {
  setFont(ctx, d.sizePx, d.bold);
  ctx.textAlign = 'right';
  ctx.fillText(d.right, g.dots - SIDE_PADDING, y);
  ctx.textAlign = 'left';
  let ny = y;
  for (const row of d.left) {
    ctx.fillText(row, SIDE_PADDING + d.indent, ny);
    ny += d.sizePx + g.lineGap;
  }
  return ny;
}

// Pinta un separador PUNTEADO (guiones con huecos), igual que el ticket de la app
// y de Electron. Se dibuja como una fila de pequeños rects para no depender del
// soporte de setLineDash y mantener el grosor exacto (g.sepThickness).
function paintSeparator(ctx: CanvasRenderingContext2D, g: Geometry, y: number): void {
  // Largo del guion y del hueco, escalados al ancho del rollo (con mínimos seguros).
  const dash = Math.max(2, Math.round(3 * g.scale));
  const gap = Math.max(2, Math.round(3 * g.scale));
  const step = dash + gap;
  const end = SIDE_PADDING + g.sepWidth;
  for (let x = SIDE_PADDING; x < end; x += step) {
    // El último guion se recorta para no rebasar el ancho de contenido.
    const w = Math.min(dash, end - x);
    ctx.fillRect(x, y, w, g.sepThickness);
  }
}

// Pasada de dibujo sobre el canvas final.
function paintDrawables(ctx: CanvasRenderingContext2D, drawables: Drawable[], g: Geometry, startY: number): void {
  let y = startY;
  for (const d of drawables) {
    if (d.type === 'sep') {
      paintSeparator(ctx, g, y);
      y += g.sepHeight;
    } else if (d.type === 'feed') {
      y += d.height;
    } else if (d.type === 'lines') {
      y = paintLines(ctx, d, g, y);
    } else {
      y = paintRow(ctx, d, g, y);
    }
  }
}

// Renderiza el ticket de un pedido a un <canvas> 2D. Devuelve el canvas, o null si
// el entorno no tiene DOM o si algo falla (para que el caller use el fallback de
// texto). Async porque puede cargar el logo.
export async function renderTicketCanvas(
  order: OrderDetail,
  options: CanvasTicketOptions,
): Promise<HTMLCanvasElement | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const g = geometryFor(options.pageWidth);

    const lines =
      options.ticketType === TICKET_TYPE_MAP.simplified.key
        ? buildSimplifiedLines(order, options)
        : buildNormalLines(order, options);

    const logo = await loadLogo(options, g.contentWidth);

    // Pasada de medición (canvas auxiliar para measureText/wrap).
    const measureCanvas = document.createElement('canvas');
    const mctx = measureCanvas.getContext('2d');
    if (!mctx) return null;
    const drawables = measureLines(mctx, lines, g, options.textSize);

    const logoBlock = logo ? logo.h + g.lineGap : 0;
    const totalHeight = SIDE_PADDING * 2 + logoBlock + drawables.reduce((sum, d) => sum + drawableHeight(d, g), 0);

    // Canvas final: fondo blanco, texto negro.
    const canvas = document.createElement('canvas');
    canvas.width = g.dots;
    canvas.height = Math.max(1, totalHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';

    let y = SIDE_PADDING;
    if (logo) {
      // putImageData copia píxeles YA leídos (del canvas aislado) → no propaga taint.
      ctx.putImageData(logo.data, Math.round((g.dots - logo.w) / 2), y);
      y += logoBlock;
    }
    paintDrawables(ctx, drawables, g, y);

    return canvas;
  } catch {
    return null;
  }
}
